import { useContext, useMemo } from 'react'
import CurrencyContext from 'contexts/currencyContext'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { UseOfferCharterCalendarDataParams, FlightCalendarData, CalendarItemBundleData } from './types'
import { useBundleOfferData } from './useBundleOfferData'
import { useBundleFlightData } from './useBundleFlightData'

/**
 * Custom hook to handle charter bundle calendar data for a single offer (FlashHotelContent)
 *
 * This hook is a pure data consumer that reads calendar and flight data from Redux.
 * It does NOT dispatch any fetch actions - it relies on the parent component
 * (e.g., FlashHotelContent.fetchCalendarData) to fetch calendar data.
 *
 * This hook handles:
 * - Accepting date ranges from caller
 * - Delegating offer calendar logic to useBundleOfferData (pure Redux reader)
 * - Delegating flight logic to useBundleFlightData (handles flight fetching)
 * - Combining hotel and flight data into bundle pricing
 * - Optimized for single offer pages (not search lists)
 *
 * @param params - Configuration parameters for offer charter calendar
 * @returns FlightCalendarData object containing bundle data for the offer
 */
export function useOfferCharterCalendarData(params: UseOfferCharterCalendarDataParams): FlightCalendarData {
  const {
    offer,
    nights,
    rooms,
    fareClass,
    dateRangesByMonth,
    multipleRoomBookingActive = false,
    charterBundleRoute,
  } = params

  const currencyContext = useContext(CurrencyContext)
  const currency: string = currencyContext || 'AUD'

  // Calculate total number of passengers
  const totalPassengers = useMemo(() => sumUpOccupancies(rooms), [rooms])

  // Only process data if charterBundleRoute is present
  const shouldFetchData = !!charterBundleRoute

  // Get offer calendar data from Redux (pure reader, no dispatch)
  const { hotelDataByDate } = useBundleOfferData({
    offer,
    nights,
    rooms,
    dateRangesByMonth: shouldFetchData ? dateRangesByMonth : {},
    multipleRoomBookingActive,
  })

  // Get flight data using the dedicated hook
  const { flightDataByDate } = useBundleFlightData({
    rooms,
    dateRangesByMonth: shouldFetchData ? dateRangesByMonth : {},
    fareClass,
    currency,
    charterBundleRoute,
  })

  // Build ready-to-use calendar item data for all dates
  const itemBundleDataByDate = useMemo(() => {
    const dataMap: Record<string, CalendarItemBundleData> = {}

    Object.values(dateRangesByMonth).flat().forEach(({ checkIn }) => {
      const flightData = flightDataByDate[checkIn]
      const hotelData = hotelDataByDate[checkIn]

      // Calculate bundle prices
      const bothDataReady = !!(hotelData?.lowestPrice && flightData?.totalPrice)
      const bundleTotalPrice = bothDataReady ? (hotelData.lowestPrice! + flightData.totalPrice!) : 0
      const bundlePricePerPerson = bothDataReady && totalPassengers > 0
        ? bundleTotalPrice / totalPassengers
        : 0

      const bundleMemberTotalPrice = bothDataReady && hotelData.lowestMemberPrice && flightData.totalPrice
        ? hotelData.lowestMemberPrice + flightData.totalPrice
        : undefined
      const bundleMemberPricePerPerson = bundleMemberTotalPrice && totalPassengers > 0
        ? bundleMemberTotalPrice / totalPassengers
        : undefined

      // Get seatsLeft from flight data
      const seatsLeft = flightData?.seatsLeft
      // Get roomsLeft from hotel data
      const roomsLeft = hotelData?.roomsLeft

      dataMap[checkIn] = {
        fetching: flightData?.fetching || hotelData?.fetching || false,
        available: bothDataReady,
        totalPrice: bundleTotalPrice,
        pricePerPerson: bundlePricePerPerson,
        memberTotalPrice: bundleMemberTotalPrice,
        memberPricePerPerson: bundleMemberPricePerPerson,
        hotelPrice: hotelData?.lowestPrice,
        hotelMemberPrice: hotelData?.lowestMemberPrice,
        flightPrice: flightData?.totalPrice,
        offerCount: hotelData?.offerCount ?? 0,
        seatsLeft,
        roomsLeft,
        hasNoPackage: hotelData?.hasNoPackage,
      }
    })

    return dataMap
  }, [dateRangesByMonth, flightDataByDate, hotelDataByDate, totalPassengers])

  return {
    itemBundleDataByDate,
  }
}
